import type { ReactNode } from "react";
import { createContext, useContext, useSyncExternalStore } from "react";
import type { Currency, Iso639 } from "../../../graphql/operations";
const Cookies = require("js-cookie");

interface Session {
  countryCode: string;
  currencyCode: Currency;
  languageCode: Iso639;
  source?: string;
}

interface SessionContextT {
  session: Session;
}

const SessionContext = createContext<SessionContextT | null>(null);

// Rendered on the server and during hydration, so the first client render
// matches the HTML regardless of the server's and the visitor's time zone or
// currency cookie. The visitor's actual values are applied right after.
const SERVER_SESSION: Session = {
  countryCode: "Asia/Tokyo",
  currencyCode: "USD" as Currency,
  // String literal instead of Iso639.En so operations.ts stays out of the shared bundle.
  languageCode: "en" as Iso639,
};

let browserSession: Session | undefined;

// Time zone and currency don't change while a page is open (changing the
// currency reloads the page), so there is nothing to subscribe to.
const subscribe = () => () => {};

const getBrowserSession = () => {
  browserSession ??= {
    countryCode: Intl.DateTimeFormat().resolvedOptions().timeZone || "",
    currencyCode: Cookies.get("currency") || "USD",
    languageCode: "en" as Iso639,
  };
  return browserSession;
};

const getServerSession = () => SERVER_SESSION;

interface Props {
  session: Session;
  children: ReactNode;
}

export function SessionProvider({ session, children }: Props) {
  return (
    <SessionContext.Provider value={{ session }}>
      {children}
    </SessionContext.Provider>
  );
}

export function useSession() {
  const provided = useContext(SessionContext);
  const session = useSyncExternalStore(
    subscribe,
    getBrowserSession,
    getServerSession,
  );

  return provided?.session ?? session;
}
